/**
 * Legal Documents tab: sub-tab switcher plus the citation-panel builder and the
 * DOM text-node walker that injects clickable citation chips into rendered docs.
 *
 * Faithful port of the monolith's showLegalTab / buildCitationPanelHTML /
 * applyCitationLinks / walkTextNodes (and the lazy citation map they rely on).
 */

import type { CaselawEntry, AuthorityType } from '@/types';
import { CASELAW } from '@/data/caselaw';
import { byId } from '@/ui/dom';
import { goToLaw } from './law';

// ── Legal sub-tab switching ────────────────────────────────────────────────
let legalActiveTab = 'motion';

/**
 * Invoke a global handler by name (motion/affidavit renderers live in the
 * not-yet-ported documents feature and are registered on window).
 * TODO: import viewMotion / viewAttyAffidavit directly once ported.
 */
function callGlobal(name: string): void {
  const fn = (window as unknown as Record<string, unknown>)[name];
  if (typeof fn === 'function') (fn as () => void)();
}

export function showLegalTab(tab: string): void {
  const tabs = ['motion', 'atty-affidavit', 'print-jurors', 'voir-dire-print'];
  tabs.forEach(function (t) {
    const el = byId('legal-' + t);
    if (el) el.style.display = t === tab ? 'flex' : 'none';
    const btn = byId('ltab-' + t);
    if (btn) btn.classList.toggle('active', t === tab);
  });
  legalActiveTab = tab;
  if (tab === 'motion') callGlobal('viewMotion');
  if (tab === 'atty-affidavit') callGlobal('viewAttyAffidavit');
}

/** Current legal sub-tab (motion | atty-affidavit | print-jurors | voir-dire-print). */
export function getLegalActiveTab(): string {
  return legalActiveTab;
}

// ── Citation panel (split doc/citation window sidebar) ─────────────────────
export function buildCitationPanelHTML(citations: CaselawEntry[], contextText: string): string {
  if (!citations || !citations.length)
    return '<p style="color:#94a3b8;font-size:12px;font-style:italic;">No applicable citations found for this document.</p>';
  const typeColors: Record<string, string> = { caselaw: '#1d4ed8', statute: '#065f46', rule: '#7c3aed' };
  const typeLabels: Record<string, string> = { caselaw: 'Case Law', statute: 'Statute', rule: 'Court Rule' };
  const highlightText = (contextText || '').toLowerCase();
  let html = '';
  citations.forEach(function (c) {
    const badgeColor = typeColors[c.type] || '#334155';
    const badgeLabel = typeLabels[c.type] || c.type;
    // Check which topics match document text for relevance annotation
    const matchedTopics = (c.topics || []).filter(function (t) {
      return highlightText.indexOf(t.toLowerCase()) > -1;
    });
    html += '<div style="border:1px solid #e2e8f0;border-radius:10px;padding:14px;margin-bottom:12px;background:#fff;">';
    // Header row: badge + citation + link
    html += '<div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">';
    html +=
      '<span style="background:' +
      badgeColor +
      ';color:#fff;border-radius:4px;padding:2px 7px;font-size:10px;font-weight:700;white-space:nowrap;flex-shrink:0;">' +
      badgeLabel +
      '</span>';
    html +=
      '<a href="' +
      c.url +
      '" target="_blank" rel="noopener" style="font-size:13px;font-weight:700;color:#1e293b;text-decoration:none;line-height:1.3;flex:1;">' +
      c.citation +
      ' <span style="font-size:10px;color:#3b82f6;">↗</span></a>';
    html += '</div>';
    // Summary
    html += '<p style="font-size:12px;color:#475569;margin:0 0 8px;line-height:1.5;">' + c.summary + '</p>';
    // Holdings (highlighted key lines)
    html +=
      '<div style="background:#f8fafc;border-left:3px solid ' +
      badgeColor +
      ';border-radius:0 6px 6px 0;padding:8px 10px;margin-bottom:8px;">';
    html +=
      '<div style="font-size:10px;font-weight:700;color:' +
      badgeColor +
      ';text-transform:uppercase;letter-spacing:0.5px;margin-bottom:5px;">Key Holdings</div>';
    c.holdings.forEach(function (h) {
      html +=
        '<div style="font-size:11px;color:#1e293b;line-height:1.6;margin-bottom:4px;padding-left:8px;border-left:2px solid #cbd5e1;">' +
        h +
        '</div>';
    });
    html += '</div>';
    // Relevance chips
    if (matchedTopics.length) {
      html += '<div style="display:flex;flex-wrap:wrap;gap:4px;">';
      matchedTopics.slice(0, 4).forEach(function (t) {
        html +=
          '<span style="background:#dbeafe;color:#1d4ed8;border-radius:12px;padding:2px 8px;font-size:10px;font-weight:600;">' +
          t +
          '</span>';
      });
      html += '</div>';
    }
    html += '</div>';
  });
  return html;
}

// ── Citation map (normalized citation text → CASELAW entry) ─────────────────
interface CitMapEntry {
  pattern: string;
  id: string;
  type: AuthorityType;
}

// Build a map: normalized-citation-text → CASELAW entry id
function buildCitationMap(): CitMapEntry[] {
  const map: CitMapEntry[] = [];
  CASELAW.forEach(function (c) {
    // Add the full citation as a match pattern
    map.push({ pattern: c.citation, id: c.id, type: c.type });
    // Also add shorter recognizable fragments (e.g. "Batson v. Kentucky", "G.L. c. 265, § 1")
    // Extract "Author v. Party" or "G.L. c. X, § Y" substrings
    const shortMatch = c.citation.match(
      /^(Commonwealth v\.\s+\w+|[A-Z][^,]+v\.\s+[^,]+|G\.L\.\s+c\.\s+[\dA-Z]+[A-Z]?,\s*§+\s*[\d\w\-]+(?:\s*et seq\.)?|Mass\.\s+R\.\s+Crim\.\s+P\.\s+[\d\w\.]+)/i
    );
    if (shortMatch && shortMatch[0] !== c.citation) {
      map.push({ pattern: shortMatch[0], id: c.id, type: c.type });
    }
  });
  // Sort longest first to prevent partial matches swallowing longer ones
  map.sort(function (a, b) {
    return b.pattern.length - a.pattern.length;
  });
  return map;
}

let _citMap: CitMapEntry[] | null = null;
function getCitationMap(): CitMapEntry[] {
  if (!_citMap) _citMap = buildCitationMap();
  return _citMap;
}

// Colour per type
const CIT_COLORS: Record<AuthorityType, { bg: string; border: string; text: string }> = {
  statute: { bg: '#dcfce7', border: '#16a34a', text: '#14532d' },
  caselaw: { bg: '#dbeafe', border: '#2563eb', text: '#1e3a8a' },
  rule: { bg: '#ede9fe', border: '#7c3aed', text: '#4c1d95' }
};

/** Escape &, <, > for safe HTML interpolation (matches the monolith's escH). */
function escH(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Apply citation highlighting to a DOM element. Safe to call multiple times —
 * skips nodes already wrapped in .cit-chip.
 */
export function applyCitationLinks(containerEl: HTMLElement | null): void {
  if (!containerEl) return;
  const map = getCitationMap();
  // Walk all text nodes inside the container
  walkTextNodes(containerEl, map);
}

interface CitPart {
  text: string;
  entryId: string | null;
  type?: AuthorityType;
}

export function walkTextNodes(node: Node, map: CitMapEntry[]): void {
  // Skip script/style/already-highlighted elements
  if (node.nodeType === 1) {
    const el = node as Element;
    if (el.classList && el.classList.contains('cit-chip')) return;
    if (
      el.tagName === 'SCRIPT' ||
      el.tagName === 'STYLE' ||
      el.tagName === 'BUTTON' ||
      el.tagName === 'A'
    )
      return;
    // Clone children list (live list mutates during iteration)
    const children = Array.from(node.childNodes);
    children.forEach(function (child) {
      walkTextNodes(child, map);
    });
    return;
  }
  if (node.nodeType !== 3) return; // only text nodes
  const text = node.nodeValue;
  if (!text || !text.trim()) return;

  // We'll build a replacement fragment if needed
  const parts: CitPart[] = []; // array of { text, entryId|null }
  let remaining = text;
  let anyHit = false;

  // Try matching patterns against remaining text, greedily
  while (remaining.length > 0) {
    let bestIdx = -1;
    let bestLen = 0;
    let bestEntry: CitMapEntry | null = null;
    for (let i = 0; i < map.length; i++) {
      const pat = map[i].pattern;
      const idx = remaining.indexOf(pat);
      if (idx !== -1) {
        if (bestIdx === -1 || idx < bestIdx || (idx === bestIdx && pat.length > bestLen)) {
          bestIdx = idx;
          bestLen = pat.length;
          bestEntry = map[i];
        }
      }
    }
    if (bestIdx === -1 || !bestEntry) {
      parts.push({ text: remaining, entryId: null });
      break;
    }
    if (bestIdx > 0) parts.push({ text: remaining.slice(0, bestIdx), entryId: null });
    parts.push({
      text: remaining.slice(bestIdx, bestIdx + bestLen),
      entryId: bestEntry.id,
      type: bestEntry.type
    });
    remaining = remaining.slice(bestIdx + bestLen);
    anyHit = true;
  }

  if (!anyHit) return; // nothing to do

  // Build replacement nodes
  const frag = document.createDocumentFragment();
  parts.forEach(function (p) {
    if (!p.entryId) {
      frag.appendChild(document.createTextNode(p.text));
    } else {
      const col = (p.type && CIT_COLORS[p.type]) || CIT_COLORS.caselaw;
      const span = document.createElement('span');
      span.className = 'cit-chip';
      span.setAttribute('data-law-id', p.entryId);
      span.title = 'Tap to view in Law tab';
      span.style.cssText = [
        'display:inline;',
        'background:' + col.bg + ';',
        'color:' + col.text + ';',
        'border:1px solid ' + col.border + ';',
        'border-radius:4px;',
        'padding:0px 4px;',
        'font-size:inherit;',
        'font-weight:600;',
        'cursor:pointer;',
        'white-space:normal;',
        'text-decoration:underline dotted;',
        'text-decoration-color:' + col.border + ';'
      ].join('');
      // Small icon after text
      span.innerHTML =
        escH(p.text) +
        '<svg style="display:inline;vertical-align:middle;margin-left:2px;" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
      const eid = p.entryId;
      span.onclick = function (e: MouseEvent): void {
        e.stopPropagation();
        goToLaw(eid);
      };
      frag.appendChild(span);
    }
  });
  const parent = node.parentNode;
  if (parent) parent.replaceChild(frag, node);
}
